import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { Box, Divider, LinkButton, mq, Text, theme } from "@pallet-hq/hegel"
import { Fade } from "react-reveal"
import { WelcomePricingSection_applylist } from "__generated__/WelcomePricingSection_applylist"
import ContingencyPlanBanner from "page-components/talent/welcome/ContigencyPlanBanner"
import { ContingencyPlanBannerApplylistFragment } from "page-components/talent/welcome/ContigencyPlanBanner/fragments"
import NextLink from "components/Link/NextLink"
import { CenteredRow, FlexRow } from "components/atoms"
import { FiCheck } from "react-icons/fi"

const PricingSectionFragment = gql`
    fragment WelcomePricingSection_applylist on ApplylistType {
        id
        showContingencyTier
        promotedPartnershipTier {
            id
            name
            description
            benefits
            monthlyItem {
                id
                uuid
                formattedPrice
                currency
                unitAmount
            }
        }
        ...ContingencyPlanBanner_applylist
    }
    ${ContingencyPlanBannerApplylistFragment}
`

type PricingSectionProps = {
    applylist: WelcomePricingSection_applylist
}

const Bullet = ({ text }: { text: string }) => (
    <FlexRow>
        <div>
            <FiCheck color={theme.colors.green} size={16} />
        </div>
        <Text variant="paragraphMedium" ml="small">
            {text}
        </Text>
    </FlexRow>
)

const PricingSection = ({ applylist }: PricingSectionProps) => {
    const { promotedPartnershipTier: promotedTier, showContingencyTier } =
        applylist
    if (!promotedTier?.monthlyItem && !showContingencyTier) return null
    const baseHref = "/hire"
    const featuredHref = `${baseHref}/setup?item=${
        promotedTier!.monthlyItem!.uuid
    }`
    const subText =
        promotedTier!.monthlyItem!.unitAmount === 0
            ? "Free, cancel anytime"
            : `Pay monthly, cancel anytime`

    return (
        <Fade>
            <Wrapper>
                <Text variant="titleLarge" textAlign="center" mb="small">
                    Fill your pipeline now
                </Text>
                <Text
                    variant="headlineMedium"
                    color="gray600"
                    textAlign="center"
                    maxWidth={630}
                    mx="auto"
                    pb="large"
                >
                    Partnering with me gives you immediate access to top
                    candidates, open to new opportunities, sourced from my
                    community
                </Text>
                {showContingencyTier ? (
                    <ContingencyPlanBanner applylist={applylist} />
                ) : (
                    <PricingBlock>
                        <Box
                            width="100%"
                            p="large"
                            style={{ boxSizing: "border-box" }}
                        >
                            <Text variant="titleMedium" mb="small">
                                {promotedTier!.name}
                            </Text>
                            <Text variant="headlineSmall" color="gray600">
                                {promotedTier!.description}
                            </Text>
                            {promotedTier!.benefits.length > 0 && (
                                <>
                                    <Divider my="medium" />
                                    <Text
                                        variant="uiSmall500"
                                        color="gray600"
                                        mb="small"
                                    >
                                        What’s included
                                    </Text>
                                    <Bullets>
                                        {promotedTier!.benefits.map(
                                            (text, index) => (
                                                <Bullet
                                                    key={index}
                                                    text={text}
                                                />
                                            )
                                        )}
                                    </Bullets>
                                </>
                            )}
                        </Box>
                        <RightBlock>
                            <Text
                                variant="headlineSmall"
                                color="gray600"
                                mb="medium"
                            >
                                {subText}
                            </Text>
                            <CenteredRow>
                                <Text variant="titleLarge" mb="medium">
                                    {promotedTier!.monthlyItem!.formattedPrice}
                                </Text>
                                <Text
                                    variant="headlineSmall"
                                    color="gray600"
                                    textTransform="uppercase"
                                    mt="xxsmall"
                                    ml="xxsmall"
                                >
                                    {promotedTier!.monthlyItem!.currency}
                                </Text>
                            </CenteredRow>
                            <StyledButton
                                href={featuredHref}
                                variant="primary"
                                size="small"
                                bg="gray700"
                                color="white"
                                mb="small"
                                width="100%"
                            >
                                Become a partner
                            </StyledButton>
                            <div>
                                <NextLink href={baseHref} passHref>
                                    <Text
                                        as="a"
                                        variant="uiSmall500"
                                        color="gray900"
                                    >
                                        Other subscription options
                                    </Text>
                                </NextLink>
                            </div>
                        </RightBlock>
                    </PricingBlock>
                )}
            </Wrapper>
        </Fade>
    )
}

PricingSection.fragment = PricingSectionFragment
export default PricingSection

const Wrapper = styled.section`
    width: 100%;
    background: ${theme.colors.gray100};
    padding: ${theme.space.xlarge} ${theme.space.small} ${theme.space.small};
    box-sizing: border-box;
`

const PricingBlock = styled.div`
    background: ${theme.colors.white};
    max-width: 1000px;
    margin: 0 auto;
    display: flex;
    border-radius: 16px;
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.16);
    ${mq({
        flexDirection: ["column", "row"],
        justifyContent: ["space-between"],
    })};
`

const RightBlock = styled.div`
    background: ${theme.colors.gray100};
    padding: ${theme.space.large};
    text-align: center;
    white-space: nowrap;
    ${mq({ borderRadius: ["0 0 16px 16px", "0 16px 16px 0"] })};
`

const StyledButton = styled(LinkButton)`
    box-sizing: border-box;
    text-transform: capitalize;
`

const Bullets = styled.div`
    display: grid;
    column-gap: ${theme.space.medium};
    row-gap: ${theme.space.xxsmall};
    ${mq({
        gridTemplateColumns: ["none", "repeat(2, 1fr)"],
    })}
`
